/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  AccessToken,
  Amounts,
  HttpStatusCode,
  TalerCorebankApi,
  TalerError,
  TalerErrorCode,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  AsyncButton,
  Loading,
  RenderAmount,
  RouteDefinition,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";

import { useBankChallengeHandlerContext } from "../../context/challenge.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../../hooks/account.js";
import { useSessionState } from "../../hooks/session.js";
import { AccountForm } from "../admin/AccountForm.js";
import { LoginForm } from "../LoginForm.js";
import { ProfileNavigation } from "../ProfileNavigation.js";
import { RetryableError } from "../../components/RetryableError.js";
import { OperationError } from "../../components/OperationError.js";
import { ReadOnlyDetail, ReadOnlyDetails } from "./ReadOnlyDetails.js";

export function ShowAccountDetails({
  account,
  routeClose,
  onUpdateSuccess,

  routeMyAccountDelete,
  routeMyAccountDetails,
  routeMyAccountMerchantIntegration,
  routeMyAccountPassword,
}: {
  routeClose: RouteDefinition;
  routeMyAccountDetails: RouteDefinition;
  routeMyAccountDelete: RouteDefinition;
  routeMyAccountMerchantIntegration: RouteDefinition;
  routeMyAccountPassword: RouteDefinition;
  onUpdateSuccess: () => void;

  account: string;
}): VNode {
  const { i18n } = useTranslationContext();
  const { state: credentials } = useSessionState();
  const sessionToken =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    config,
    lib: { bank },
  } = useBankCoreApiContext();
  const accountIsTheCurrentUser =
    credentials.status === "loggedIn"
      ? credentials.username === account
      : false;

  const mfa = useBankChallengeHandlerContext();

  const [submitAccount, setSubmitAccount] = useState<
    TalerCorebankApi.AccountReconfiguration | undefined
  >();
  const { showError, displayInfo } = useNotificationContext();

  const result = useAccountDetails(account);

  // i18n.str`update account`,
  const update = useNotifiedOperation<
    Awaited<ReturnType<typeof bank.updateAccount>>,
    [
      string,
      AccessToken,
      TalerCorebankApi.AccountReconfiguration,
      challengeIds?: string[],
    ]
  >(
    (
      ct,
      username: string,
      token: AccessToken,
      account: TalerCorebankApi.AccountReconfiguration,
      challengeIds?: string[],
    ) => bank.updateAccount({ username, token }, account, { challengeIds }),
    {
      onSuccess: async () => {
        mfa.cancel();
        await revalidateAccountDetails();
        displayInfo(i18n.str`Account updated`);
        onUpdateSuccess();
      },
      onFail: showError(
        i18n.str`Failed to update the account.`,
        (fail, username, token, account) => {
          switch (fail.case) {
            case HttpStatusCode.Unauthorized:
              return i18n.str`The rights to change the account are not sufficient`;
            case HttpStatusCode.NotFound:
              return i18n.str`The username was not found`;
            case TalerErrorCode.BANK_NON_ADMIN_PATCH_LEGAL_NAME:
              return i18n.str`You can't change the legal name, please contact your account administrator.`;
            case TalerErrorCode.BANK_NON_ADMIN_PATCH_DEBT_LIMIT:
              return i18n.str`You can't change the debt limit, please contact your account administrator.`;
            case TalerErrorCode.BANK_NON_ADMIN_PATCH_CASHOUT:
              return i18n.str`You can't change the cashout address, please contact your account administrator.`;
            case TalerErrorCode.BANK_MISSING_TAN_INFO:
              return i18n.str`No information for the selected authentication channel.`;
            case HttpStatusCode.Accepted:
              mfa.onNewChallenge(
                i18n.str`Account update`,
                username,
                fail.body,
                {
                  running: update.running,
                  cancel: update.cancel,
                  run: (challengeIds) =>
                    update.run(username, token, account, challengeIds),
                },
              );
              return undefined;
            case TalerErrorCode.BANK_TAN_CHANNEL_NOT_SUPPORTED:
              return i18n.str`Authentication channel is not supported.`;
            case TalerErrorCode.BANK_NON_ADMIN_SET_CONVERSION_RATE_CLASS:
              return i18n.str`Only the administrator can change the conversion rate.`;
            case TalerErrorCode.BANK_CONVERSION_RATE_CLASS_UNKNOWN:
              return i18n.str`The conversion rate class doesn't exist.`;
            case TalerErrorCode.BANK_PASSWORD_TOO_SHORT:
              return i18n.str`The password is too short. Can't have less than 8 characters.`;
            case TalerErrorCode.BANK_PASSWORD_TOO_LONG:
              return i18n.str`The password is too long. Can't have more than 64 characters.`;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  if (!result) {
    return <Loading />;
  }
  if (result instanceof TalerError) {
    return (
      <Fragment>
        <RetryableError
          error={result}
          title={i18n.str`Failed to load account details.`}
          onRetry={() => void revalidateAccountDetails()}
        />
        <LoginForm currentUser={account} />
      </Fragment>
    );
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
      case HttpStatusCode.NotFound:
        return <LoginForm currentUser={account} />;
      default:
        assertUnreachable(result);
    }
  }

  const userIsAdmin =
    credentials.status === "loggedIn" && credentials.isUserAdministrator;
  const nameIsEditable = config.allow_edit_name || userIsAdmin;
  const cashoutIsEditable = config.allow_edit_cashout_payto_uri || userIsAdmin;

  return (
    <Fragment>
      {accountIsTheCurrentUser ? (
        <ProfileNavigation
          current="details"
          routeOverview={routeClose}
          routeMyAccountDetails={routeMyAccountDetails}
          routeMyAccountMerchantIntegration={routeMyAccountMerchantIntegration}
          routeMyAccountPassword={routeMyAccountPassword}
        />
      ) : (
        <h1 class="text-base font-semibold leading-6 text-onBackground">
          <i18n.Translate>Account "{account}"</i18n.Translate>
        </h1>
      )}

      {result.body.status !== "deleted" ? undefined : (
        <Attention title={i18n.str`Removed`} type="info">
          <i18n.Translate>This account can't be used.</i18n.Translate>
        </Attention>
      )}

      <div class="my-4 grid grid-cols-1 gap-x-8 gap-y-8 rounded-lg bg-gray-100 px-4 pb-4 pt-6 md:grid-cols-3">
        <div class="px-4 sm:px-0">
          <h2 class="text-base font-semibold leading-7 text-onBackground">
            <i18n.Translate>Account details</i18n.Translate>
          </h2>
        </div>
        <ReadOnlyDetails>
          <ReadOnlyDetail label={i18n.str`Login username`} value={account} />
          {nameIsEditable ? undefined : (
            <ReadOnlyDetail
              label={i18n.str`Full name`}
              value={result.body.name}
            />
          )}
          <ReadOnlyDetail
            label={i18n.str`Account address`}
            value={result.body.payto_uri}
            copyValue={result.body.payto_uri}
            description={i18n.str`Copy and share this account address to receive transfers.`}
          />
          {!config.allow_conversion || cashoutIsEditable ? undefined : (
            <ReadOnlyDetail
              label={i18n.str`Cashout account`}
              value={result.body.cashout_payto_uri ?? i18n.str`Not configured`}
              copyValue={result.body.cashout_payto_uri}
            />
          )}
          {userIsAdmin ? undefined : (
            <ReadOnlyDetail
              label={i18n.str`Max debt`}
              value={
                <RenderAmount
                  value={Amounts.parseOrThrow(result.body.debit_threshold)}
                  spec={config.currency_specification}
                />
              }
            />
          )}
        </ReadOnlyDetails>
      </div>

      <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-6 md:grid-cols-3 bg-gray-100 my-4 px-4 pb-4 rounded-lg">
        <div class="px-4 sm:px-0">
          <h2 class="text-base font-semibold leading-7 text-onBackground">
            <div class="flex items-center justify-between">
              <span class="flex flex-grow flex-col">
                <span class="text-sm text-onBackground font-semibold leading-6 ">
                  <i18n.Translate>Change details</i18n.Translate>
                </span>
              </span>
            </div>
          </h2>
        </div>

        <AccountForm
          focus={true}
          username={account}
          template={result.body}
          purpose="update"
          hideNonEditableFields
          onChange={(a) => setSubmitAccount(a)}
        >
          <div class="border-t border-onBackground/10 px-4 py-4 sm:px-8">
            <OperationError class="mb-4" />
            <div class="flex items-center justify-between gap-x-6">
              <a
                href={routeClose.url({})}
                name="cancel"
                class="text-sm font-semibold leading-6 text-onBackground"
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </a>
              <AsyncButton
                submit
                name="update"
                class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                disabled={!sessionToken || !submitAccount}
                onClick={() =>
                  update.run(account, sessionToken!, submitAccount!)
                }
              >
                <i18n.Translate>Update</i18n.Translate>
              </AsyncButton>
            </div>
          </div>
        </AccountForm>
      </div>
      {accountIsTheCurrentUser &&
      config.allow_deletions &&
      result.body.status !== "deleted" ? (
        <section class="mt-8 rounded-lg border border-red-200 bg-red-50 p-5">
          <h2 class="text-base font-semibold text-red-900">
            <i18n.Translate>Delete account</i18n.Translate>
          </h2>
          <p class="mt-2 text-sm text-red-800">
            <i18n.Translate>
              Permanently remove this bank account. The account must have a zero
              balance before it can be deleted.
            </i18n.Translate>
          </p>
          <a
            href={routeMyAccountDelete.url({})}
            class="mt-4 inline-flex rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
          >
            <i18n.Translate>Delete account</i18n.Translate>
          </a>
        </section>
      ) : undefined}
    </Fragment>
  );
}
